/**
 * HistorySourceDataDialog.js
 *
 * Magnifying-glass dialog for Adult Autism History line items.
 * Shows the original form question text, the respondent's answer, and
 * available options — matching the ADHD Clinical Interview (CIQuestionPanel) UX.
 */
import React, { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/outline';
import { SearchIcon, ClockIcon } from '@heroicons/react/solid';
import { FormattedAnswer } from '../../../../utils/formDisplayUtils';
import { formatDateTime } from '../../../../utils/timestampUtils';
import { resolveHistorySourceDialogRows } from './getHistorySourcePrefillInputs';

function isOptionSelected(option, rawAnswer) {
  if (rawAnswer === null || rawAnswer === undefined || rawAnswer === '') return false;

  if (typeof rawAnswer === 'string') {
    return rawAnswer === option;
  }

  if (Array.isArray(rawAnswer)) {
    if (typeof rawAnswer[0] === 'string') {
      return rawAnswer.includes(option);
    }
    if (typeof rawAnswer[0] === 'object') {
      return rawAnswer.some((ans) => ans.value === option);
    }
  }

  if (typeof rawAnswer === 'object' && 'value' in rawAnswer) {
    return rawAnswer.value === option;
  }

  return false;
}

/**
 * @param {object} props
 * @param {string[]} props.varNames - Source variable names for this column
 * @param {object} props.formInstances - formKey -> formInstance map
 * @param {string} props.dialogTitle - e.g. "Patient Form Responses"
 * @param {string} props.lineItemTitle - History question title
 * @param {string|null} props.displayPrefillText - Formatted prefill shown in the column
 * @param {string|null} props.lastUpdatedAt - Prefill timestamp if available
 * @param {Array|null} props.tabs - Multi-informant mode: array of { id, label, varNames, formInstances, displayPrefillText, lastUpdatedAt }
 */
export default function HistorySourceDataDialog({
  varNames,
  formInstances,
  dialogTitle,
  lineItemTitle,
  displayPrefillText,
  lastUpdatedAt,
  patientFirstName = '',
  tabs = null,
  /** Accessible name for the trigger — set it when one row renders more than
   *  one magnifier (e.g. a column per informant) so the buttons stay
   *  distinguishable. Defaults to the historical label. */
  triggerLabel = null,
}) {
  const [open, setOpen] = useState(false);
  const [activeTabIdx, setActiveTabIdx] = useState(0);

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    // Always reopen on the first informant tab (assignment order).
    if (!nextOpen) setActiveTabIdx(0);
  };

  // Single-informant data resolution — computed eagerly so the dialog opens at
  // full size (no post-open content growth flash).
  const rawPrefillData = useMemo(() => {
    if (tabs) return [];
    return resolveHistorySourceDialogRows(
      varNames,
      formInstances,
      displayPrefillText,
      patientFirstName,
      lineItemTitle,
    );
  }, [varNames, formInstances, displayPrefillText, patientFirstName, lineItemTitle, tabs]);

  // Multi-informant tabbed data resolution
  const tabbedPrefillData = useMemo(() => {
    if (!tabs) return [];
    return tabs.map((tab) => ({
      ...tab,
      data: resolveHistorySourceDialogRows(
        tab.varNames,
        tab.formInstances,
        tab.displayPrefillText,
        patientFirstName,
        lineItemTitle,
      ),
    }));
  }, [tabs, patientFirstName, lineItemTitle]);

  if (!varNames?.length) return null;

  // Determine which data to render inside the dialog body
  const activeData = tabs
    ? (tabbedPrefillData[activeTabIdx]?.data || [])
    : rawPrefillData;
  const activeDisplayText = tabs
    ? (tabbedPrefillData[activeTabIdx]?.displayPrefillText || null)
    : displayPrefillText;
  const activeLastUpdatedAt = tabs
    ? (tabbedPrefillData[activeTabIdx]?.lastUpdatedAt || null)
    : lastUpdatedAt;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="flex-shrink-0 mt-2 flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100"
          aria-label={triggerLabel || `View source form responses for ${lineItemTitle}`}
        >
          <SearchIcon className="h-3 w-3" />
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
        {/*
          Use a full-viewport flex shell + inner card (radix-dialog-content-inset-motion)
          instead of top-1/2 left-1/2 + translate + scaleIn. The centered-motion class
          animates transform on the same node as Tailwind translate utilities, which
          makes the overlay and panel appear to grow/jump on open. Inset-motion fades
          opacity only — matching FormDetailsPanel and ADHD Patient Overview dialogs.
        */}
        <Dialog.Content
          className="radix-dialog-content-inset-motion fixed inset-0 z-50 flex items-center justify-center p-4 outline-none"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="w-[95vw] max-w-[700px] max-h-[85vh] md:max-h-[65vh] overflow-y-auto rounded-lg bg-white p-4 md:p-6 shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              {dialogTitle}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className="p-1 rounded-full hover:bg-gray-100">
                <XIcon className="h-5 w-5 text-gray-500" />
              </button>
            </Dialog.Close>
          </div>

          <div className="mb-4">
            <h3 className="font-semibold text-sm mb-2">{lineItemTitle}</h3>

            {/* Informant tabs — shown only in multi-informant mode */}
            {tabs && tabs.length > 1 && (
              <div className="flex border-b border-gray-200 mb-3">
                {tabs.map((tab, idx) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                      idx === activeTabIdx
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTabIdx(idx)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {activeLastUpdatedAt && (
              <div className="text-xs text-gray-500 mb-3 flex items-center">
                <ClockIcon className="h-3 w-3 mr-1" />
                Last updated: {formatDateTime(activeLastUpdatedAt)}
              </div>
            )}

            <div className="space-y-2 mt-2">
              {activeData.length > 0 ? (
                activeData.map((item, i) => (
                  <div
                    key={i}
                    className={`border rounded-md p-3 ${
                      item.hasAnswer
                        ? 'bg-violet-50 shadow-sm border-violet-200'
                        : 'bg-gray-50 border-dashed border-gray-200 opacity-75'
                    }`}
                  >
                    <div className="text-xs text-gray-500 mb-0.5">
                      From: {item.formName}
                      {item.isStoredPrefillFallback && (
                        <span className="text-gray-400"> (stored response)</span>
                      )}
                    </div>

                    {item.questionTitle && (
                      <div className="text-xs font-medium text-gray-700 mb-1">{item.questionTitle}</div>
                    )}

                    <div className="text-xs font-medium mb-1.5">{item.question}</div>

                    {item.wasSkipped && (
                      <div className="text-xs text-gray-500 flex justify-start items-center mb-2">
                        <span>Respondent clicked</span>
                        <span className="text-xs bg-violet-50 shadow-sm border-violet-200 border text-gray-800 font-semibold px-2 py-1 rounded-md ml-2 whitespace-nowrap">
                          {item.skipButtonText}
                        </span>
                      </div>
                    )}

                    <div className={`p-2 rounded border whitespace-pre-wrap text-xs ${
                      item.hasAnswer
                        ? 'bg-white border-gray-200'
                        : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
                    }`}
                    >
                      <FormattedAnswer
                        rawAnswer={item.rawAnswer}
                        displayText={
                          activeData.length === 1 && activeDisplayText
                            ? activeDisplayText
                            : item.answer
                        }
                        inputType={item.inputType}
                      />
                    </div>

                    {item.options?.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-gray-500 mb-1">Available options:</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                          {item.options.map((option, optIndex) => (
                            <div
                              key={optIndex}
                              className={`text-xs p-1.5 rounded ${
                                item.hasAnswer && isOptionSelected(option, item.rawAnswer)
                                  ? 'bg-violet-100 border border-violet-300 text-violet-700 font-medium'
                                  : 'bg-gray-50 border border-gray-200 text-gray-500'
                              }`}
                            >
                              {option}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">No source data available</p>
              )}
            </div>
          </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
